import { TableTitles } from "@/constants";
import {
	useDeleteMeditationRetreat,
	useGetMeditationRetreats,
} from "@/hooks/query-hooks/useMeditationRetreats";
import { cn } from "@/libs/cn";
import { DatePicker, Divider, Flex, Space } from "antd";
import dayjs from "dayjs";
import { useState } from "react";
import { Helmet } from "react-helmet-async";
import {
	AnimatedComponent,
	DeleteConfirmationModal,
	PageTitle,
} from "../common";
import { AddNewMeditationRetreat } from "./AddNewMeditationRetreat";
import { EditMeditationRetreat } from "./EditMeditationRetreat";
import { MeditationRetreatsTable } from "./MeditationRetreatsTable";

export function MeditationRetreatsByYear({
	Component,
	isInDashboard = false,
}: {
	Component?: React.FC;
	isInDashboard?: boolean;
}) {
	const [currentYear, setCurrentYear] = useState(dayjs().year());
	const [page, setPage] = useState(1);

	const { data, isLoading, isFetching } = useGetMeditationRetreats(
		page,
		currentYear,
	);

	const retreats = data?.content ?? [];

	const onYearChange = (date: dayjs.Dayjs | null) => {
		if (date) {
			setCurrentYear(date.year());
		}
	};

	const dashboardRenderColumnKey = [
		TableTitles.retreat_start_month,
		TableTitles.retreat_level,
		TableTitles.retreat_name,
		TableTitles.retreat_duration,
		TableTitles.retreat_end_date,
		TableTitles.retreat_start_date,
		TableTitles.retreat_status,
		TableTitles.total_registrants,
		TableTitles.retreat_rooms
	];

	const renderColumnsKey = [
		TableTitles.retreat_start_month,
		TableTitles.retreat_level,
		TableTitles.retreat_name,
		TableTitles.retreat_duration,
		TableTitles.retreat_end_date,
		TableTitles.retreat_start_date,
		TableTitles.retreat_status,
		TableTitles.total_registrants,
		TableTitles.retreat_rooms,
		TableTitles.action,
	];

	return (
		<AnimatedComponent>
			<Helmet>
				<title>တရားစခန်းများ</title>
				<meta
					name="description"
					content="လစဉ်အလိုက်ကျင်းပမည့် တရားစခန်းများ"
				/>
			</Helmet>
			<Flex justify="space-between" align="center" gap={12}>
				<PageTitle
					className={cn({
						"text-base": Component, // base font size for Dashboard & Pages with Component prop
					})}
				>
					လစဉ်အလိုက်ကျင်းပမည့် တရားစခန်းများ
				</PageTitle>

				{Component ? <Component /> : <AddNewMeditationRetreat />}
			</Flex>
			<Divider />
			<div className="!space-y-4">
				<DatePicker.YearPicker
					size="large"
					defaultValue={dayjs()}
					onChange={onYearChange}
					allowClear={false}
					inputReadOnly
					className="w-24 !rounded-md"
				/>
				<MeditationRetreatsTable
					dataSource={retreats}
					renderColumnKeys={
						isInDashboard
							? dashboardRenderColumnKey
							: renderColumnsKey
					}
					TableActionComponent={TableActionComponent}
					loading={isLoading || isFetching}
					pagination={{
						current: page,
						onChange: (page) => setPage(page),
						total: data?.totalElements,
						pageSize: data?.size,
					}}
				/>
			</div>
		</AnimatedComponent>
	);
}

const TableActionComponent = ({ record }: { record: MeditationRetreat }) => {
	const { mutate, isLoading } = useDeleteMeditationRetreat();

	return (
		<Space>
			<EditMeditationRetreat {...record} />
			<DeleteConfirmationModal
				onDelete={() => mutate(record.id)}
				isDeleting={isLoading}
			/>
		</Space>
	);
};
